import { AI_GUIDE_KEYS } from '@repo/lib';
import { EDITOR_SECTIONS, editorSections, editorTabLabel } from '../../pageEditTabs';
import { DEFINITIONS } from './aiGuideDefinitions';

const withDefaults = definition => ({ coverage: 'full', ...definition });

export const GUIDES = Object.fromEntries(
  Object.entries(AI_GUIDE_KEYS).map(([pageType, keys]) => [
    pageType,
    Object.fromEntries(
      keys.map(key => {
        const definition = DEFINITIONS[key];
        return [
          key,
          typeof definition === 'function'
            ? withDefaults(definition(pageType))
            : definition && withDefaults(definition),
        ];
      }),
    ),
  ]),
);

const EDITOR_TABS = [...new Set(EDITOR_SECTIONS.map(section => section.tab))];

export const guideExamples = (definition, context) =>
  typeof definition.examples === 'function' ? definition.examples(context) : definition.examples;

function toGuide(key, definition, section, context) {
  const { path, sectionLabel, ...rest } = definition;
  return {
    key,
    command: `/${key}`,
    ...rest,
    examples: guideExamples(definition, context),
    section: { path, label: sectionLabel ?? section.label },
  };
}

export function guidesFor({ page, seller, themes }) {
  const definitions = Object.entries(GUIDES[page?.type] ?? {});
  if (definitions.length === 0) return [];

  return EDITOR_TABS.map(tab => ({
    tab,
    tabLabel: editorTabLabel(tab, page.type),
    // A screen can hold more than one guide: Form fields has the fields and the discount code.
    guides: editorSections(tab, { page, seller }).flatMap(section =>
      definitions
        .filter(([, definition]) => definition.tab === tab && definition.path === section.path)
        .map(([key, definition]) => toGuide(key, definition, section, { page, seller, themes })),
    ),
  })).filter(group => group.guides.length > 0);
}

export function filterGuideGroups(groups, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) return groups;

  return groups
    .map(group => ({
      ...group,
      guides: group.guides.filter(
        guide => guide.key.includes(needle) || guide.section.label.toLowerCase().includes(needle),
      ),
    }))
    .filter(group => group.guides.length > 0);
}

export function findSectionGuide(groups, tab, path) {
  return groups.find(group => group.tab === tab)?.guides.find(guide => guide.section.path === path);
}

export function findGuide(groups, key) {
  for (const group of groups) {
    const guide = group.guides.find(candidate => candidate.key === key);
    if (guide) return guide;
  }
  return undefined;
}
